import { generateKeyPairFromSeed } from '@stablelib/ed25519';
import * as ucans from '@ucans/ucans';
import { fromUint8Array } from 'js-base64';
import hkdf from 'futoin-hkdf';
import { HKDF_KEY_INFO, SIGN_IN_MESSAGE } from '../constants';
import {
  getIdentitySignedMessage,
  getSession,
  updateSession,
  updateUserIdentitySignedMessage,
} from '@/db/queries';
import { toHex, hashMessage } from 'viem';
import { privateKeyToAccount } from 'viem/accounts';
import { getSmartAccountClient } from '../account-utils';
import { TWalletClient } from './types';
import argon2 from 'argon2-browser';
import { toAESKey } from './aes-utils';

enum HKDFKeyInfo {
  MASTER_KEY = 'MASTER_KEY',
  SIGNING_KEY = 'SIGNING_KEY',
  AGENT_KEY = 'AGENT_KEY',
}

const DERIVATION_KEY_CONFIG = {
  [HKDFKeyInfo.MASTER_KEY]: {
    length: 32,
    hash: 'SHA-256',
  },
  [HKDFKeyInfo.AGENT_KEY]: {
    length: 32,
    hash: 'SHA-256',
  },
  [HKDFKeyInfo.SIGNING_KEY]: {
    length: 32,
    hash: 'SHA-256',
  },
};

const createDerivedKey = (
  secret: string,
  salt: Uint8Array,
  info: HKDFKeyInfo
) => {
  const { length, hash } = DERIVATION_KEY_CONFIG[info];
  const privateKey = hkdf(Buffer.from(secret), length, {
    salt: Buffer.from(salt),
    info: Buffer.from(info),
    hash: hash,
  });
  return Uint8Array.from(privateKey);
};

export const buildMasterKey = async (ag2Hash: string, salt: Uint8Array) => {
  const derivedKey = createDerivedKey(ag2Hash, salt, HKDFKeyInfo.MASTER_KEY);
  return toAESKey(derivedKey);
};

export const buildAgentKey = (ag2Hash: string, salt: Uint8Array) => {
  return createDerivedKey(ag2Hash, salt, HKDFKeyInfo.AGENT_KEY);
};

export const buildSigningKey = (ag2Hash: string, salt: Uint8Array) => {
  const signingKey = createDerivedKey(ag2Hash, salt, HKDFKeyInfo.SIGNING_KEY);
  const { secretKey: ucanSecret } = generateKeyPairFromSeed(signingKey);

  return ucans.EdKeypair.fromSecretKey(fromUint8Array(ucanSecret), {
    exportable: true,
  });
};

export const getLegacyDerivedKey = (signedMessage: string) =>
  hkdf(Buffer.from(signedMessage), 32, {
    info: Buffer.from(HKDF_KEY_INFO),
  });

export const getAg2Hash = async (pass: string, salt: Uint8Array) => {
  const key = await argon2.hash({
    pass,
    salt,
    type: argon2.ArgonType.Argon2id,
    hashLen: 32,
    time: 2,
    mem: 102400,
    parallelism: 8,
  });
  return fromUint8Array(key.hash);
};

export const getAgent = async (keyMaterial: Uint8Array) => {
  const privateAccount = privateKeyToAccount(toHex(keyMaterial));

  return await getSmartAccountClient(privateAccount);
};

export const getLegacySignedMessage = async (walletClient: TWalletClient) => {
  const cachedSignedMessage = await getSession(walletClient.account.address);

  if (cachedSignedMessage && cachedSignedMessage.signedMessage)
    return cachedSignedMessage.signedMessage;
  else {
    const signedMessage = await walletClient.signMessage({
      message: hashMessage(SIGN_IN_MESSAGE),
    });

    await updateSession(walletClient.account.address, signedMessage);
    return signedMessage;
  }
};

export const getLegacyAgent = async (walletClient: TWalletClient) => {
  const signedMessage = await getLegacySignedMessage(walletClient);
  const derivedKey = getLegacyDerivedKey(signedMessage);
  return getAgent(derivedKey);
};

export const getNewAgent = async (agHash: string, salt: Uint8Array) => {
  const derivedKey = buildAgentKey(agHash, salt);
  return getAgent(derivedKey);
};

export const getNewSignedMessage = async (walletClient: TWalletClient) => {
  const cachedSignedMessage = await getIdentitySignedMessage(
    walletClient.account.address
  );
  if (cachedSignedMessage && cachedSignedMessage.signedMessage)
    return cachedSignedMessage.signedMessage;
  else {
    const signedMessage = await walletClient.signMessage({
      message: SIGN_IN_MESSAGE,
    });
    await updateUserIdentitySignedMessage(
      walletClient.account.address,
      signedMessage
    );
    return signedMessage;
  }
};
